import { Box, TextField, ToggleButtonGroup, Typography } from '@mui/material';
import React from 'react';
import { MAX_IMPORT_PAGES } from './documentImportLimits';
import {
  StyledToggleButton,
  TOGGLE_GROUP_HOVER_SX,
  TOGGLE_GROUP_SELECTED_BORDER_SX,
  TOGGLE_GROUP_WIDTH_SX,
} from '../../styles/StyledButtons';

export type DocumentImportScopeMode = 'document' | 'page' | 'range' | 'region';

const SCOPE_TOGGLE_SX = [
  TOGGLE_GROUP_HOVER_SX,
  TOGGLE_GROUP_SELECTED_BORDER_SX,
  TOGGLE_GROUP_WIDTH_SX,
];

interface DocumentImportScopeBarProps {
  mode: DocumentImportScopeMode;
  fromPage: number;
  toPage: number;
  pageCount: number;
  lastImportedRange?: { fromPage: number; toPage: number } | null;
  disabled?: boolean;
  onModeChange: (mode: DocumentImportScopeMode) => void;
  onFromPageChange: (page: number) => void;
  onToPageChange: (page: number) => void;
}

export default function DocumentImportScopeBar({
  mode,
  fromPage,
  toPage,
  pageCount,
  lastImportedRange,
  disabled = false,
  onModeChange,
  onFromPageChange,
  onToPageChange,
}: DocumentImportScopeBarProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={mode}
        disabled={disabled}
        onChange={(_, value: DocumentImportScopeMode | null) => value && onModeChange(value)}
        aria-label="Import selection"
      >
        <StyledToggleButton mini value="document" sx={SCOPE_TOGGLE_SX}>
          Whole document
        </StyledToggleButton>
        <StyledToggleButton mini value="range" sx={SCOPE_TOGGLE_SX}>
          Page range
        </StyledToggleButton>
        <StyledToggleButton mini value="page" sx={SCOPE_TOGGLE_SX}>
          Current page
        </StyledToggleButton>
        <StyledToggleButton mini value="region" sx={SCOPE_TOGGLE_SX}>
          Part of page
        </StyledToggleButton>
      </ToggleButtonGroup>
      <Typography variant="caption" color="text.secondary">
        {mode === 'document' &&
          (pageCount > MAX_IMPORT_PAGES
            ? `Import pages 1–${MAX_IMPORT_PAGES} now. You can import the remaining pages in batches of up to ${MAX_IMPORT_PAGES}.`
            : 'Import the whole document with AI.')}
        {mode === 'page' && 'Import the current page with AI.'}
        {mode === 'range' &&
          (lastImportedRange
            ? `Pages ${lastImportedRange.fromPage}–${lastImportedRange.toPage} imported. Pages ${fromPage}–${toPage} are selected next; adjust the range or import with AI.`
            : `Import up to ${MAX_IMPORT_PAGES} pages at a time with AI.`)}
        {mode === 'region' && 'Drag the box to import that part of the page with AI.'}
      </Typography>
      {mode === 'range' && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            label="From"
            type="number"
            size="small"
            value={fromPage}
            inputProps={{ min: 1, max: pageCount }}
            disabled={disabled}
            onChange={(event) => onFromPageChange(parseInt(event.target.value, 10) || 1)}
          />
          <TextField
            label="To"
            type="number"
            size="small"
            value={toPage}
            inputProps={{
              min: fromPage,
              max: Math.min(pageCount, fromPage + MAX_IMPORT_PAGES - 1),
            }}
            disabled={disabled}
            onChange={(event) => onToPageChange(parseInt(event.target.value, 10) || 1)}
          />
        </Box>
      )}
    </Box>
  );
}
